import { useEffect, useMemo, useState } from 'react';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../components/DataTable/BasicPagination';
import PageSizeSelector from '../../components/DataTable/PageSizeSelector';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import BranchService from '../../services/branch/BranchService';
import { useAuthUser } from '../../hooks/auth/useAuthUser';
import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import useRestaurantDiningRooms from '../../hooks/restaurant/useRestaurantDiningRooms';
import useDebounced from '../../hooks/useDebounced';
import {
  RestaurantDiningRoomDTO,
  RestaurantDiningRoomFilters,
} from '../../types/restaurant/RestaurantDiningRoomTypes';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';

import {
  DiningRoomHeader,
  DiningRoomBranchEmpty,
  DiningRoomStats,
  DiningRoomFilters,
  DiningRoomSkeleton,
  DiningRoomEmpty,
  DiningRoomGrid,
  DiningRoomTable,
  DiningRoomFormModal,
  DiningRoomQuickTableModal,
} from '../../features/restaurant/diningRooms';
import WorkspaceLayout from '../../layout/WorkspaceLayout';

const RestaurantDiningRooms = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [branchError, setBranchError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounced(search, 350);
  // Al limpiar (también al cambiar sucursal) no conserva temporalmente el
  // término anterior mientras vence el debounce.
  const normalizedSearch = search === '' ? '' : debouncedSearch;

  const diningRooms = useRestaurantDiningRooms(branchId);
  const [tableForRoom, setTableForRoom] = useState<RestaurantDiningRoomDTO | null>(
    null
  );

  useEffect(() => {
    diningRooms.resetResults();
    return diningRooms.resetResults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadBranches = async () => {
      setLoadingBranches(true);
      setBranchError('');

      try {
        const data = canPickBranch
          ? await BranchService.fetchDropdown()
          : [await BranchService.fetchMyBranch()];
        setBranches(data);
        if (data.length) setBranchId(data[0].id);
      } catch (error: any) {
        setBranchError(
          error?.response?.data?.message ||
            'No fue posible cargar las sucursales.'
        );
      } finally {
        setLoadingBranches(false);
      }
    };

    loadBranches();
  }, [canPickBranch]);

  const filters = useMemo<RestaurantDiningRoomFilters>(
    () => ({
      search: normalizedSearch.trim() || null,
      active: status === 'ALL' ? null : status === 'ACTIVE',
      page,
      size: pageSize,
    }),
    [normalizedSearch, page, pageSize, status]
  );

  useEffect(() => {
    if (branchId) diningRooms.load(filters, diningRooms.page !== null);
    // Las respuestas antiguas son ignoradas por el hook mediante requestId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, filters]);

  const activeCount = diningRooms.list.filter((room) => room.active).length;

  const changeBranch = (value: number) => {
    diningRooms.resetResults();
    setSearch('');
    setStatus('ALL');
    setPage(0);
    setBranchId(value);
  };

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const changeStatus = (value: 'ALL' | 'ACTIVE' | 'INACTIVE') => {
    setStatus(value);
    setPage(0);
  };

  const retry = () => diningRooms.load(filters, diningRooms.page !== null);

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <DiningRoomHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          branchError={branchError}
          canPickBranch={canPickBranch}
          onBranchChange={changeBranch}
          onCreate={diningRooms.openCreate}
        />

        {!loadingBranches && !branchError && branches.length === 0 ? (
          <DiningRoomBranchEmpty />
        ) : branchId ? (
          <>
            <DiningRoomStats
              total={diningRooms.page?.totalElements ?? 0}
              active={activeCount}
            />

            <DiningRoomFilters
              search={search}
              status={status}
              onSearchChange={changeSearch}
              onStatusChange={changeStatus}
            />

            {diningRooms.loading ? (
              <DiningRoomSkeleton />
            ) : diningRooms.error && !diningRooms.page ? (
              <div className="flex min-h-[300px] flex-col items-center justify-center px-6 text-center">
                <span className="mb-4 rounded-full bg-danger/10 p-4 text-danger">
                  <AlertCircle className="h-8 w-8" />
                </span>
                <h3 className="text-lg font-semibold text-black dark:text-white">
                  No fue posible cargar los salones
                </h3>
                <p className="mt-2 text-sm text-bodydark2">
                  {diningRooms.error}
                </p>
                <button
                  type="button"
                  onClick={retry}
                  className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-medium text-white"
                >
                  <RefreshCw className="h-4 w-4" /> Reintentar
                </button>
              </div>
            ) : diningRooms.list.length === 0 ? (
              <DiningRoomEmpty />
            ) : (
              <div className="relative">
                {diningRooms.error && (
                  <div className="m-5 flex items-center justify-between gap-3 rounded-lg border border-danger/20 bg-danger/5 px-4 py-3 text-sm text-danger">
                    <span>{diningRooms.error}</span>
                    <button
                      type="button"
                      onClick={retry}
                      className="font-semibold underline"
                    >
                      Reintentar
                    </button>
                  </div>
                )}
                {diningRooms.updating && (
                  <div className="absolute right-5 top-3 z-10 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-primary shadow dark:bg-boxdark">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />{' '}
                    Actualizando
                  </div>
                )}
                <DiningRoomTable
                  rooms={diningRooms.list}
                  onEdit={diningRooms.edit}
                  onDeactivate={(room) =>
                    diningRooms.deactivate(room, filters, () =>
                      setPage(page - 1)
                    )
                  }
                  onAddTable={setTableForRoom}
                />
                <DiningRoomGrid
                  rooms={diningRooms.list}
                  onEdit={diningRooms.edit}
                  onDeactivate={(room) =>
                    diningRooms.deactivate(room, filters, () =>
                      setPage(page - 1)
                    )
                  }
                  onAddTable={setTableForRoom}
                />
              </div>
            )}

            {diningRooms.page && diningRooms.page.totalElements > 0 && (
              <div className="flex flex-col gap-4 border-t border-stroke px-5 pb-5 dark:border-strokedark sm:flex-row sm:items-end sm:justify-between">
                <BasicPagination
                  currentPage={diningRooms.page.number}
                  totalPages={diningRooms.page.totalPages}
                  onPageChange={setPage}
                />
                <PageSizeSelector
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(0);
                  }}
                />
              </div>
            )}
          </>
        ) : null}
      </SectionWrapper>

      <DiningRoomFormModal
        isOpen={diningRooms.modalOpen}
        form={diningRooms.form}
        saving={diningRooms.saving}
        onClose={diningRooms.closeModal}
        onChange={diningRooms.updateField}
        onSave={() => diningRooms.save(filters)}
      />

      <DiningRoomQuickTableModal
        room={tableForRoom}
        onClose={() => setTableForRoom(null)}
        onCreate={diningRooms.createTableInline}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantDiningRooms;
